
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Plus, 
  Search, 
  MessageSquare, 
  Calendar,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Wand2,
  FileText
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import ChatActions from './history/ChatActions';

interface ChatSidebarProps {
  onNewChat: () => void;
  onSelectChat: (chatId: string) => void;
  currentChatId?: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  currentMode: 'free' | 'enhance' | 'template';
}

interface ChatSession {
  id: string;
  title: string;
  updated_at: string;
  template_title?: string;
  message_count: number;
}

const ProductCopilotChatSidebar: React.FC<ChatSidebarProps> = ({
  onNewChat,
  onSelectChat,
  currentChatId,
  isCollapsed = false,
  onToggleCollapse,
  currentMode
}) => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadSessions();
    }
  }, [user]);

  const loadSessions = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .rpc('get_chat_sessions_with_details', { current_user_id: user.id });

      if (error) throw error;

      const formattedSessions = data?.map((session: any) => ({
        id: session.id,
        title: session.title,
        updated_at: session.updated_at,
        template_title: session.template_title,
        message_count: Number(session.message_count)
      })) || [];

      setSessions(formattedSessions);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSessions = sessions.filter(session => 
    searchQuery === '' || 
    session.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleRename = (sessionId: string, newTitle: string) => {
    setSessions(prev => prev.map(session => 
      session.id === sessionId 
        ? { ...session, title: newTitle }
        : session
    ));
  };

  const handleDuplicate = (newSessionId: string) => {
    loadSessions();
  };

  const handleDelete = (sessionId: string) => {
    setSessions(prev => prev.filter(s => s.id !== sessionId));
  };

  const getModeConfig = (mode: string) => {
    switch (mode) {
      case 'enhance':
        return { icon: Wand2, color: 'text-green-600', bg: 'bg-green-100' };
      case 'template':
        return { icon: FileText, color: 'text-purple-600', bg: 'bg-purple-100' };
      default:
        return { icon: Sparkles, color: 'text-blue-600', bg: 'bg-blue-100' };
    }
  };

  const currentModeConfig = getModeConfig(currentMode);
  const CurrentModeIcon = currentModeConfig.icon;

  if (isCollapsed) {
    return (
      <div className="w-16 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col items-center py-4 h-full">
        {/* Expand Button */}
        <Button
          onClick={onToggleCollapse}
          variant="ghost"
          size="sm"
          className="w-10 h-10 p-0 mb-4"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        
        {/* New Chat Button */}
        <Button
          onClick={onNewChat}
          size="sm"
          className={cn("w-10 h-10 p-0 mb-4", currentModeConfig.bg, currentModeConfig.color)}
        >
          <Plus className="h-4 w-4" />
        </Button>
        
        {/* Mode Indicator */}
        <div className={cn("w-10 h-10 rounded-lg mb-4 flex items-center justify-center", currentModeConfig.bg)}>
          <CurrentModeIcon className={cn("h-4 w-4", currentModeConfig.color)} />
        </div>
        
        {/* Recent Chats */}
        <div className="flex flex-col gap-2">
          {filteredSessions.slice(0, 5).map((session) => (
            <Button
              key={session.id}
              variant={currentChatId === session.id ? "default" : "ghost"}
              size="sm"
              className="w-10 h-10 p-0"
              onClick={() => onSelectChat(session.id)}
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Product Copilot
          </h2>
          <Button
            onClick={onToggleCollapse}
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Current Mode Indicator */}
        <div className={cn("p-3 rounded-lg mb-4 border", 
          currentMode === 'enhance' && "bg-green-50 border-green-200",
          currentMode === 'template' && "bg-purple-50 border-purple-200",
          currentMode === 'free' && "bg-blue-50 border-blue-200"
        )}>
          <div className="flex items-center gap-2 mb-1">
            <CurrentModeIcon className={cn("h-4 w-4", currentModeConfig.color)} />
            <span className={cn("font-medium text-sm", currentModeConfig.color)}>
              {currentMode === 'enhance' ? 'Enhance Mode' : 
               currentMode === 'template' ? 'Template Mode' : 'Free Chat'}
            </span>
          </div>
          <p className="text-xs opacity-75">
            {currentMode === 'enhance' ? 'Transform ideas into powerful prompts' :
             currentMode === 'template' ? 'Use pre-built templates for guidance' :
             'Natural conversation with AI'}
          </p>
        </div>
        
        <Button
          onClick={onNewChat}
          className="w-full justify-start gap-2 mb-4"
          size="lg"
        >
          <Plus className="h-4 w-4" />
          New Chat
        </Button>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Chat History */}
      <ScrollArea className="flex-1">
        <div className="p-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No conversations yet</p>
              <p className="text-xs">Start a new chat to begin</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredSessions.map((session) => (
                <Card
                  key={session.id}
                  className={cn(
                    "p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group relative",
                    currentChatId === session.id && "ring-2 ring-primary bg-accent"
                  )}
                  onClick={() => onSelectChat(session.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 pr-2">
                      <div className="flex items-center gap-2 mb-1">
                        <MessageSquare className="h-3 w-3 text-primary flex-shrink-0" />
                        <h3 className="text-sm font-medium truncate">
                          {session.title}
                        </h3>
                      </div>
                      
                      {session.template_title && (
                        <div className="flex items-center gap-1 mb-1">
                          <FileText className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground truncate">
                            Template: {session.template_title}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {formatDistanceToNow(new Date(session.updated_at), { addSuffix: true })}
                        </div>
                        
                        <Badge variant="secondary" className="text-xs">
                          {session.message_count} messages
                        </Badge>
                      </div>
                    </div>
                    
                    <div 
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ChatActions
                        sessionId={session.id}
                        currentTitle={session.title}
                        onRename={handleRename}
                        onDuplicate={handleDuplicate}
                        onDelete={handleDelete}
                      />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default ProductCopilotChatSidebar;
